/**
 * Improvements — triage for improvement suggestions
 * (experts.improvement_suggestions).
 *
 * 2026-06-06 de-GitHub: suggestions used to become GitHub issues through an
 * App that was never installable, so the submit_improvement MCP tool and
 * chat thumbs-down feedback silently 500'd. They are product data; they land
 * in PG via /api/learning/submit and admins triage them HERE.
 *
 * Source of truth: apps/experts/api/admin/improvements.js
 *   GET  /api/admin/improvements[?status=pending|closed] → Improvement[]
 *   POST /api/admin/improvements { id, action: 'close'|'reopen', comment? }
 *
 * EVERY call goes through the data-access hooks → the vendored `api` wrapper →
 * the single auth resolver seam (same discipline as every admin page).
 */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@andavo/web-ui';
import {
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Panel,
} from '../../components/primitives';
import {
  useImprovementAction,
  useImprovements,
} from '../../data-access/useAdmin';
import { absTime } from '../../lib/format';
import type { Improvement, ImprovementStatus } from '../../types';

const STATUS_FILTERS: { value: ImprovementStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'closed', label: 'Closed' },
];

const SOURCE_BADGE: Record<string, string> = {
  mcp: 'bg-blue-500/15 text-blue-700',
  chat_feedback: 'bg-purple-500/15 text-purple-700',
};

function SuggestionCard({ item }: { item: Improvement }) {
  const action = useImprovementAction();
  const pending = item.status === 'pending';

  const close = () => {
    const comment = window.prompt(
      'Resolution note (optional):',
      item.close_comment ?? '',
    );
    if (comment === null) return; // cancelled
    action.mutate(
      { id: item.id, action: 'close', comment: comment || undefined },
      {
        onSuccess: () => toast.success(`Closed #${item.id}.`),
        onError: (err) =>
          toast.error((err as Error)?.message ?? 'Action failed'),
      },
    );
  };

  const reopen = () => {
    action.mutate(
      { id: item.id, action: 'reopen' },
      {
        onSuccess: () => toast.success(`Reopened #${item.id}.`),
        onError: (err) =>
          toast.error((err as Error)?.message ?? 'Action failed'),
      },
    );
  };

  return (
    <div
      className="rounded-md border border-border-subtle bg-surface-foreground p-4"
      data-testid="improvement-card"
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="font-sans font-medium text-content-primary text-sm">
          #{item.id} · {item.title}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 font-inter font-medium text-xs ${
            pending
              ? 'bg-amber-500/15 text-amber-700'
              : 'bg-green-500/15 text-green-700'
          }`}
        >
          {item.status}
        </span>
      </div>
      {item.description ? (
        <div className="whitespace-pre-wrap font-inter text-content-secondary text-sm">
          {item.description}
        </div>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-2 font-inter text-content-tertiary text-xs">
        <span>Submitted {absTime(item.created_at)}</span>
        {item.submitted_by ? <span>· by {item.submitted_by}</span> : null}
        {item.source ? (
          <span
            className={`rounded-full px-2 py-0.5 font-medium ${
              SOURCE_BADGE[item.source] ??
              'bg-content-tertiary/15 text-content-secondary'
            }`}
          >
            {item.source}
          </span>
        ) : null}
        {(item.labels ?? []).map((l) => (
          <span
            className="rounded-full bg-content-tertiary/10 px-2 py-0.5"
            key={l}
          >
            {l}
          </span>
        ))}
      </div>
      {item.close_comment ? (
        <div className="mt-2 font-inter text-content-secondary text-sm italic">
          Resolution: {item.close_comment}
        </div>
      ) : null}
      <div className="mt-3">
        {pending ? (
          <Button
            data-testid="improvement-close"
            disabled={action.isPending}
            onClick={close}
            type="button"
          >
            {action.isPending ? 'Closing…' : 'Close'}
          </Button>
        ) : (
          <Button
            data-testid="improvement-reopen"
            disabled={action.isPending}
            onClick={reopen}
            type="button"
            variant="secondary"
          >
            {action.isPending ? 'Reopening…' : 'Reopen'}
          </Button>
        )}
      </div>
    </div>
  );
}

export function ImprovementsPage() {
  const [status, setStatus] = useState<ImprovementStatus | ''>('');
  const { data, isPending, isError, error, refetch, isFetching } =
    useImprovements(status);

  const items = data ?? [];
  const pendingCount = useMemo(
    () => items.filter((i) => i.status === 'pending').length,
    [items],
  );

  return (
    <div data-testid="improvements-page">
      <AdminPageHeader
        actions={
          <button
            className="rounded-md border border-border-subtle px-3 py-1.5 font-inter text-content-secondary text-sm hover:bg-surface-background"
            onClick={() => void refetch()}
            type="button"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        }
        subtitle="Improvement suggestions from the MCP submit_improvement tool and chat feedback. Product data — triaged here, not on GitHub."
        title="Improvements"
      />

      <div className="mb-4 flex items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            className={`rounded-md px-2.5 py-1 font-inter text-sm transition-colors ${
              status === f.value
                ? 'bg-surface-action-subtle text-content-action'
                : 'text-content-secondary hover:bg-surface-background'
            }`}
            key={f.value || 'all'}
            onClick={() => setStatus(f.value)}
            type="button"
          >
            {f.label}
          </button>
        ))}
        {data ? (
          <span
            className="ml-auto font-inter text-content-tertiary text-xs"
            data-testid="improvement-count"
          >
            {items.length} suggestion{items.length === 1 ? '' : 's'}
            {pendingCount > 0 ? ` · ${pendingCount} pending` : ''}
          </span>
        ) : null}
      </div>

      <Panel>
        {isPending ? <AdminLoading /> : null}
        {isError ? (
          <AdminError
            message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
          />
        ) : null}
        {data && items.length === 0 ? (
          <AdminEmpty>No improvement suggestions found.</AdminEmpty>
        ) : null}
        {data && items.length > 0 ? (
          <div className="flex flex-col gap-3">
            {items.map((item) => (
              <SuggestionCard item={item} key={item.id} />
            ))}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

export default ImprovementsPage;
